import React, { useEffect, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import Table from "../../Components/Script/Table";
import { getAllServices, toggleServiceStatus } from "../../Services/dynamicservice";

function AllServicesAdmin() {
    const [services, setServices] = useState([]);
    const [expandedServiceId, setExpandedServiceId] = useState(null);
    const navigate = useNavigate();
    const [loadingServiceId, setLoadingServiceId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | ACTIVE | INACTIVE
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });


    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
    };

    const filteredServices = services
        .filter(service => {
            const matchesSearch =
                service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                service.slug.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus =
                statusFilter === "ALL" ||
                (statusFilter === "ACTIVE" && service.isActive) ||
                (statusFilter === "INACTIVE" && !service.isActive);

            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            if (!sortConfig.key) return 0;

            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];

            if (typeof valA === "string") valA = valA.toLowerCase();
            if (typeof valB === "string") valB = valB.toLowerCase();

            if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
            if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });


    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        const data = await getAllServices();
        setServices(data);
    };

    const toggleFormPreview = (serviceId) => {
        setExpandedServiceId(prev =>
            prev === serviceId ? null : serviceId
        );
    };

    const handleEdit = (serviceId) => {
        navigate(`/services/edit/${serviceId}`);
    };

    const handleToggleActive = async (service) => {
        setLoadingServiceId(service.id);

        try {
            await toggleServiceStatus(service.id);

            setServices(prev =>
                prev.map(s =>
                    s.id === service.id
                        ? { ...s, isActive: !s.isActive }
                        : s
                )
            );
        } catch {
            alert("Failed to update status");
        } finally {
            setLoadingServiceId(null);
        }
    };



    const getPriceByType = (prices, type) => {
        if (!prices || prices.length === 0) return "-";

        const priceObj = prices.find(p => p.userType === type);
        return priceObj ? `₹ ${priceObj.price}` : "-";
    };


    const renderFieldPreview = (field) => {
        switch (field.type) {
            case "textarea":
                return <textarea className="form-control" disabled />;
            case "select":
                return (
                    <select className="form-control" disabled>
                        <option>Select</option>
                        {field.options?.map(o => (
                            <option key={o}>{o}</option>
                        ))}
                    </select>
                );
            case "radio":
                return field.options?.map(o => (
                    <div className="form-check" key={o}>
                        <input type="radio" className="form-check-input" disabled />
                        <label className="form-check-label">{o}</label>
                    </div>
                ));
            case "checkbox":
                return (
                    <div className="form-check">
                        <input type="checkbox" className="form-check-input" disabled />
                        <label className="form-check-label">{field.label}</label>
                    </div>
                );
            default:
                return <input type={field.type} className="form-control" disabled />;
        }
    };

    return (
        <>
            <Table />

            <div className="wrapper">
                <div className="content-page">
                    <div className="content">
                        <div className="container-fluid">

                            {/* PAGE TITLE */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="page-title-box">
                                        <div className="page-title-right">
                                            <ol className="breadcrumb m-0">
                                                <li className="breadcrumb-item">
                                                    <Link to="/dashboard">Dashboard</Link>
                                                </li>
                                                <li className="breadcrumb-item active">Services</li>
                                            </ol>
                                        </div>
                                        <h4 className="page-title">All Services</h4>
                                    </div>
                                </div>
                            </div>

                            {/* TABLE */}
                            <div className="card">
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search by name or slug..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <select
                                                className="form-select"
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                            >
                                                <option value="ALL">All Services</option>
                                                <option value="ACTIVE">Active</option>
                                                <option value="INACTIVE">Inactive</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Edit</th>
                                                    <th>Active</th>
                                                    <th onClick={() => handleSort("parent")} style={{ cursor: "pointer" }}>
                                                        Parent {sortConfig.key === "parent" && (sortConfig.direction === "asc" ? "▲" : "▼")}
                                                    </th>
                                                    <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                                                        Name {sortConfig.key === "name" && (sortConfig.direction === "asc" ? "▲" : "▼")}
                                                    </th>
                                                    <th>Slug</th>
                                                    <th>User Price</th>
                                                    <th>Agent Price</th>
                                                    <th>Form</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {filteredServices.length > 0 ? (
                                                    filteredServices.map(service => (
                                                        <React.Fragment key={service.id}>
                                                            {/* MAIN ROW */}
                                                            <tr>
                                                                <td>
                                                                    <button
                                                                        className="btn btn-outline-primary btn-sm border-0"
                                                                        title="Edit"
                                                                        onClick={() => handleEdit(service.id)}
                                                                    >
                                                                        <i className="mdi mdi-pencil"></i>
                                                                    </button>

                                                                </td>

                                                                <td>
                                                                    <div className="form-check form-switch">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            role="switch"
                                                                            checked={service.isActive}
                                                                            disabled={loadingServiceId === service.id}
                                                                            onChange={() => handleToggleActive(service)}
                                                                        />

                                                                    </div>
                                                                </td>

                                                                <td>{service.parentService?.name || "-"}</td>
                                                                <td>{service.name}</td>
                                                                <td>{service.slug}</td>
                                                                <td>{getPriceByType(service.prices, "USER")}</td>
                                                                <td>{getPriceByType(service.prices, "AGENT")}</td>

                                                                <td>
                                                                    <button
                                                                        className="btn btn-sm btn-info"
                                                                        onClick={() => toggleFormPreview(service.id)}
                                                                    >
                                                                        👁 View Form
                                                                    </button>
                                                                </td>
                                                            </tr>

                                                            {/* EXPANDED FORM PREVIEW */}
                                                            {expandedServiceId === service.id && (
                                                                <tr className="bg-light">
                                                                    <td colSpan="6">
                                                                        <h6 className="mb-3">Form Preview</h6>

                                                                        {JSON.parse(service.formSchema || "[]").map(field => (
                                                                            <div key={field.id} className="mb-3">
                                                                                <label className="form-label">
                                                                                    {field.label}
                                                                                    {field.required && (
                                                                                        <span className="text-danger"> *</span>
                                                                                    )}
                                                                                </label>
                                                                                {renderFieldPreview(field)}
                                                                            </div>
                                                                        ))}
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            No Services Found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>

                                        </table>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <Outlet />
        </>
    );
}

export default AllServicesAdmin;
